/**
 * BacktestStepProgress.tsx
 *
 * Enhanced step progress indicator with pulsing glow effect on active step.
 * Shows Events/Sec counter for simulation speed.
 */

import React from 'react';
import { ProgressStep } from '../../types/backtest';

interface BacktestStepProgressProps {
  steps: ProgressStep[];
  currentMessage: string;
  eventsPerSecond: number;
}

const BacktestStepProgress: React.FC<BacktestStepProgressProps> = ({
  steps,
  currentMessage,
  eventsPerSecond
}) => {
  const getStepIcon = (status: ProgressStep['status']) => {
    switch (status) {
      case 'complete':
        return (
          <span className="bt-step-ic is-done" aria-label="Complete">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </span>
        );
      case 'running':
        return <span className="bt-step-ic is-run" aria-label="Running" />;
      default:
        return <span className="bt-step-ic is-pending" aria-label="Pending" />;
    }
  };

  const stepClass = (status: ProgressStep['status']): string =>
    status === 'running' ? 'is-run' : status === 'complete' ? 'is-done' : 'is-pending';

  return (
    <div className="bt-panel">
      {/* Header */}
      <div className="bt-panel-h">
        <span className="bt-h">Progress</span>
        <span className="meta">{currentMessage}</span>
      </div>

      {/* Steps List */}
      <div className="bt-steps">
        {steps.map((step) => (
          <div key={step.id} className={`bt-step ${stepClass(step.status)}`}>
            {/* Status Icon */}
            {getStepIcon(step.status)}

            {/* Step Label */}
            <div className="bt-step-b">
              <div className="bt-step-l">{step.label}</div>

              {/* Progress Bar (only for running step) */}
              {step.status === 'running' && (
                <div className="bt-step-bar">
                  <i style={{ width: `${step.progress}%` }} />
                </div>
              )}
            </div>

            {/* Progress Percentage */}
            {step.status === 'running' && (
              <div className="bt-step-pct n">{step.progress}%</div>
            )}
          </div>
        ))}
      </div>

      {/* Events/Sec Counter */}
      <div className="bt-panel-f">
        <span>Events/Sec</span>
        <span className={`n ${eventsPerSecond > 0 ? 'is-t1' : 'is-t3'}`}>
          {eventsPerSecond.toFixed(1)}
        </span>
      </div>
    </div>
  );
};

export default BacktestStepProgress;
